import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { MODULES } from '../config/modules.config';
import type { ModuleConfig } from '../config/modules.config';

// Declare the global config type
declare global {
  interface Window {
    PanLumaWidgetConfig?: {
      reporter?: {
        name?: string;
        email?: string;
      };
      product?: string;
      /** Set to false to show name/email fields even when pre-filled. Default: hide when both are provided. */
      hideReporterFields?: boolean;
      /** Bearer token forwarded to the widget submit endpoint so the backend can attribute the ticket to the signed-in user. */
      authToken?: string;
      /**
       * Explicit widget language hint. The embeddable widget resolves this first
       * (before <html lang>/navigator.language), so dogfooding our own SPA always
       * shows the widget in the user's active app language — reliable regardless of
       * the widget's auto-detect timing. The widget normalizes e.g. "he-IL" -> "he"
       * and ignores unsupported languages (falling back to its configured default).
       */
      language?: string;
    };
  }
}

/**
 * Resolve which module a page path belongs to, using the canonical module
 * registry (`basePaths` from `modules_entities.config.json`) so URL→module
 * resolution is single-sourced and covers multi-path modules (email, files,
 * shipments) automatically. Returns `undefined` when no non-home module matches
 * (e.g. the home dashboard `/`).
 */
function getWidgetModuleForPath(pathname: string): ModuleConfig | undefined {
  for (const module of MODULES) {
    // `home` has basePath `/`, which prefix-matches everything — skip it so it
    // never shadows a real module (and `/` itself resolves to no module).
    if (module.id === 'home') continue;
    for (const basePath of module.basePaths) {
      // Match on a full segment boundary so `/sales` doesn't match `/salesfoo`.
      if (pathname === basePath || pathname.startsWith(`${basePath}/`)) {
        return module;
      }
    }
  }
  return undefined;
}

/**
 * Frontend-registry module ids that resolve from a URL prefix but are NOT
 * customer-facing products, so no ticket product should be set on their pages.
 * The dogfood support tenant configures a master-data product only for real
 * product modules; sending an internal-area slug would just fail to resolve
 * (harmless), but we exclude them so the widget stays cleanly unset there —
 * preserving the prior "no product on non-product areas" behaviour. `home` is
 * already skipped inside {@link getWidgetModuleForPath}.
 */
const NON_PRODUCT_MODULE_IDS = new Set<string>([
  'sysadmin',
  'developer',
  'coding',
  'design-system',
]);

/**
 * Resolve the current page path to the product **slug** the embedded widget
 * should send, or `undefined` when the page has no product module.
 *
 * The slug is the first segment of the URL path (e.g. `/pipeline/deals/42` →
 * `pipeline`, `/master-data/products` → `master-data`). The dogfood support
 * tenant (sysadmin.panluma.ai) has self-configured its master-data products so
 * each product's **SKU equals this slug**, and the backend widget path resolves
 * the submitted `product` string by name-or-SKU
 * (`WidgetService._resolve_product_id`). Sending the URL slug therefore routes
 * the ticket to the right product without a hand-maintained name table.
 *
 * Gated by {@link getWidgetModuleForPath} so only a recognised product module's
 * pages set a product; internal areas (see {@link NON_PRODUCT_MODULE_IDS}), the
 * home dashboard, and unknown paths return `undefined`.
 */
export function getWidgetProductForPath(pathname: string): string | undefined {
  const module = getWidgetModuleForPath(pathname);
  if (!module || NON_PRODUCT_MODULE_IDS.has(module.id)) return undefined;
  // First non-empty path segment = the module slug the SKU is keyed on. The
  // matched module's basePath is always a segment-boundary prefix of the path,
  // so this segment is that module's slug (e.g. an /onedrive route on the files
  // module yields `onedrive`).
  const slug = pathname.split('/').filter(Boolean)[0];
  return slug ? slug.toLowerCase() : undefined;
}

/**
 * Component that configures the PanLuma Support Widget with the current user's info
 * and the current module context as a product name.
 *
 * Place this component in your app layout after authentication is established.
 */
export function SupportWidgetConfig() {
  const { user, accessToken } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (user) {
      window.PanLumaWidgetConfig = {
        reporter: {
          name: user.full_name || user.email,
          email: user.email,
        },
        product: getWidgetProductForPath(location.pathname),
        authToken: accessToken ?? undefined,
        // Render the widget in the user's active app language (dogfood reliability).
        language: i18n.language,
      };
    } else {
      window.PanLumaWidgetConfig = undefined;
    }

    return () => {
      window.PanLumaWidgetConfig = undefined;
    };
  }, [user, accessToken, location.pathname, i18n.language]);

  return null;
}
